import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { Box, Typography } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import dayjs from "dayjs";
import { CostTableSource, MassDenominator } from "src/store/api/generatedApi";
import { useMemo } from "react";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { GroupedByCostSource } from "../types";
import { defaultTableSx } from "../tableFormatting";
import { useTranslation } from "react-i18next";

type Row = {
  id: string;
  sourceName: string;
} & Record<MassDenominator, number | null>;

interface Props {
  tableSource: CostTableSource;
  tableData: GroupedByCostSource;
  selectedDate: Date;
}

export const CostTable = ({ tableSource, tableData, selectedDate }: Props) => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const units = useUnitsFormatter(true);
  const columns: GridColDef<Row>[] = useMemo(() => {
    return [
      { field: "sourceName", headerName: tenantT("source"), flex: 1 },
      ...tableData.availableMassDenominators.map((massDenominator) => ({
        field: massDenominator,
        headerName: t(massDenominator),
        flex: 1,
        type: "number" as const,
        valueFormatter: (value?: number) =>
          !value ? "" : formatNumber(2, true)(value),
      })),
    ];
  }, [t, tableData, formatNumber, tenantT]);

  const rows: Row[] = useMemo(() => {
    return tableData.availableSources.map((source, index) => {
      const costs = typeSafeObjectFromEntries(
        tableData.availableMassDenominators.map((massDenominator) => [
          massDenominator,
          tableData.seriesCollection
            .find(
              (s) => s.source === source && s.massDenominator == massDenominator
            )
            ?.items.find(({ date }) => {
              const parsedDate = dayjs.utc(date);
              const parsedSelectedDate = dayjs.utc(selectedDate);
              return (
                parsedDate.month() === parsedSelectedDate.month() &&
                parsedDate.year() === parsedSelectedDate.year()
              );
            })?.cost ?? null,
        ])
      );

      return {
        id: `${tableSource}-${source}-${index}`,
        sourceName: t(source),
        ...costs,
      };
    });
  }, [tableData, selectedDate, tableSource, t]);

  const gridHeight = 28 + rows.length * 32;

  return (
    <Box width={350}>
      <Typography
        variant="h6"
        fontWeight="fontWeightBold"
        mb={1}
        whiteSpace="nowrap"
      >
        {t(tableSource)} {units("specific_cost")}
      </Typography>
      <Box>
        <HotkeyCompliantDataGrid
          columns={columns}
          rows={rows}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnResize
          disableVirtualization
          sx={{ ...defaultTableSx, height: gridHeight, maxHeight: 400 }}
        />
      </Box>
    </Box>
  );
};
